import React, { useCallback, useEffect, useRef, useState } from 'react';
import { signSupplierDoc } from '../../lib/consoleVerification';

// In-page document review for the verification file. Nothing here opens a new
// tab: reviewing a supplier used to mean leaving the page for every single
// file, losing your place and your scroll position each time.
//
// supplier-docs URLs are signed and therefore expire. A long review outlasts a
// signature, so freshness is handled twice over: proactively (anything older
// than STALE_AFTER_MS is re-signed the moment it is opened) and reactively (an
// <img>/<video> that fails to load re-signs itself once). A broken document is
// never what the reviewer sees first.

const TTL_SECONDS = 60 * 60;
const STALE_AFTER_MS = 45 * 60 * 1000;

export function useSignedDocs(docs) {
  const [urls, setUrls] = useState({});      // id → signed url
  const signedAt = useRef({});               // id → epoch ms
  const retries = useRef({});                // id → count, caps the error loop
  const [failed, setFailed] = useState({});  // id → true once retries are spent

  const signOne = useCallback(async (doc) => {
    const url = await signSupplierDoc(doc.raw, TTL_SECONDS);
    if (url) {
      signedAt.current[doc.id] = Date.now();
      setUrls((prev) => ({ ...prev, [doc.id]: url }));
      setFailed((prev) => (prev[doc.id] ? { ...prev, [doc.id]: false } : prev));
    } else {
      setFailed((prev) => ({ ...prev, [doc.id]: true }));
    }
    return url;
  }, []);

  useEffect(() => {
    let alive = true;
    signedAt.current = {};
    retries.current = {};
    setFailed({});
    setUrls({});
    (async () => {
      const entries = await Promise.all(docs.map(async (d) => [d.id, await signSupplierDoc(d.raw, TTL_SECONDS)]));
      if (!alive) return;
      const now = Date.now();
      const next = {};
      for (const [id, url] of entries) if (url) { next[id] = url; signedAt.current[id] = now; }
      setUrls(next);
    })();
    return () => { alive = false; };
    // Keyed on the raw storage paths, NOT the doc ids: ids are column names, so
    // two suppliers who each uploaded exactly a licence and a factory photo
    // produce the identical id list, and keying on that would leave the second
    // supplier showing the first one's signed documents. Raw paths carry the
    // owner's uuid, so they change whenever the file does.
  }, [docs.map((d) => d.raw).join('|')]); // eslint-disable-line react-hooks/exhaustive-deps

  // Called when a media element reports a load failure. Re-signs once, then
  // gives up and lets the card offer a manual retry.
  const onLoadError = useCallback((doc) => {
    const count = retries.current[doc.id] || 0;
    if (count >= 1) { setFailed((prev) => ({ ...prev, [doc.id]: true })); return; }
    retries.current[doc.id] = count + 1;
    signOne(doc);
  }, [signOne]);

  // Called before showing a document full-size. A signature past STALE_AFTER_MS
  // is replaced up front rather than waiting for it to break.
  const ensureFresh = useCallback((doc) => {
    if (!doc) return;
    const at = signedAt.current[doc.id];
    if (!at || Date.now() - at > STALE_AFTER_MS) signOne(doc);
  }, [signOne]);

  const retry = useCallback((doc) => { retries.current[doc.id] = 0; signOne(doc); }, [signOne]);

  return { urls, failed, onLoadError, ensureFresh, retry };
}

function KindGlyph({ kind, size = 26 }) {
  const d = kind === 'pdf'
    ? 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M9 15h6'
    : kind === 'video'
      ? 'M23 7l-7 5 7 5zM1 5h15v14H1z'
      : 'M4 5h16v14H4zM4 15l4-4 3 3 3-4 6 7M9 9.5a1.2 1.2 0 1 1-2.4 0 1.2 1.2 0 0 1 2.4 0';
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={d} />
    </svg>
  );
}

export function DocCard({ doc, url, failed, checked, onToggle, onOpen, onLoadError, onRetry, isAr }) {
  const label = isAr ? doc.ar : doc.en;
  return (
    <div className={`acv-doc${checked ? ' checked' : ''}`}>
      <button type="button" className="acv-doc-thumb" onClick={() => onOpen(doc)}
              aria-label={`${label} — ${isAr ? 'عرض' : 'view'}`}>
        {url && doc.kind === 'image' ? (
          <img src={url} alt={label} loading="lazy" onError={() => onLoadError(doc)} />
        ) : url && doc.kind === 'video' ? (
          <video src={url} muted playsInline preload="metadata" onError={() => onLoadError(doc)} />
        ) : (
          <span className="acv-doc-glyph"><KindGlyph kind={doc.kind} /></span>
        )}
        {doc.kind !== 'image' && <span className="acv-doc-kind">{doc.kind.toUpperCase()}</span>}
      </button>

      <div className="acv-doc-foot">
        <label className="acv-check">
          <input type="checkbox" checked={checked} onChange={() => onToggle(doc.id)} />
          <span className="acv-check-box" aria-hidden="true">{checked ? '✓' : ''}</span>
          <span className="acv-doc-name" title={label}>
            {label}{doc.required && <span className="acv-req"> *</span>}
          </span>
        </label>
      </div>

      {failed && (
        <button type="button" className="acv-doc-retry" onClick={() => onRetry(doc)}>
          {isAr ? 'تعذّر التحميل — إعادة المحاولة' : 'Failed to load — retry'}
        </button>
      )}
    </div>
  );
}

export function DocLightbox({ docs, urls, failed, index, onIndex, onClose, onLoadError, onRetry, checked, onToggle, isAr }) {
  const doc = docs[index];
  const url = doc ? urls[doc.id] : null;

  useEffect(() => {
    if (!doc) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onIndex(index + 1);
      if (e.key === 'ArrowLeft') onIndex(index - 1);
      if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); onToggle(doc.id); }
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prevOverflow; };
  }, [doc, index, onClose, onIndex, onToggle]);

  if (!doc) return null;
  const label = isAr ? doc.ar : doc.en;
  const isChecked = checked.has(doc.id);

  return (
    <div className="acv-lb" onClick={onClose} role="dialog" aria-modal="true" aria-label={label}>
      <div className="acv-lb-bar" onClick={(e) => e.stopPropagation()}>
        <span className="acv-lb-title">{label}</span>
        <span className="acv-lb-count">{index + 1} / {docs.length}</span>
        <button type="button" className={`ac-btn ac-btn-sm${isChecked ? ' ac-btn-primary' : ''}`} onClick={() => onToggle(doc.id)}>
          {isChecked ? (isAr ? '✓ تمت مراجعتها' : '✓ Reviewed') : (isAr ? 'تعليم كمراجَعة' : 'Mark reviewed')}
        </button>
        <button type="button" className="acv-lb-x" onClick={onClose} aria-label={isAr ? 'إغلاق' : 'Close'}>✕</button>
      </div>

      <button type="button" className="acv-lb-nav prev" disabled={index === 0}
              onClick={(e) => { e.stopPropagation(); onIndex(index - 1); }} aria-label={isAr ? 'السابق' : 'Previous'}>‹</button>

      <div className="acv-lb-stage" onClick={(e) => e.stopPropagation()}>
        {!url ? (
          <div className="acv-lb-msg">
            <p>{isAr ? 'تعذّر تحميل هذه الوثيقة.' : 'This document could not be loaded.'}</p>
            <button type="button" className="ac-btn ac-btn-sm" onClick={() => onRetry(doc)}>{isAr ? 'إعادة المحاولة' : 'Retry'}</button>
          </div>
        ) : doc.kind === 'pdf' ? (
          <iframe src={url} title={label} className="acv-lb-frame" />
        ) : doc.kind === 'video' ? (
          <video src={url} controls autoPlay playsInline className="acv-lb-media" onError={() => onLoadError(doc)} />
        ) : (
          <img src={url} alt={label} className="acv-lb-media" onError={() => onLoadError(doc)} />
        )}
        {failed[doc.id] && url && (
          <button type="button" className="ac-btn ac-btn-sm acv-lb-refresh" onClick={() => onRetry(doc)}>
            {isAr ? 'تحديث الرابط' : 'Refresh link'}
          </button>
        )}
      </div>

      <button type="button" className="acv-lb-nav next" disabled={index >= docs.length - 1}
              onClick={(e) => { e.stopPropagation(); onIndex(index + 1); }} aria-label={isAr ? 'التالي' : 'Next'}>›</button>
    </div>
  );
}
